import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePunishment } from '../../../contexts/PunishmentContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useTickets } from '../../../contexts/TicketContext';
import { AlertCircle, Gavel, CheckCircle } from 'lucide-react';
import { PUNISHMENT_TYPES } from '../../../utils/punishmentUtils'; // Ensure this exists or mock it if not found
import { getStaffDisplayName } from '../../../utils/staffDisplay';

const QuickPunishmentModal = ({ isOpen, onClose, targetId, targetCity, ticketId }) => {
    const { addPunishment } = usePunishment();
    const { user } = useAuth();
    const staffName = getStaffDisplayName(user);
    const { addTicketResponse, updateTicket } = useTickets();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        type: 'Advertência',
        reason: '',
        duration: '',
        durationType: 'days',
        notes: ''
    });

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.reason) return alert('Informe o motivo!');

        setLoading(true);
        try {
            // Apply Punishment
            const punishmentData = {
                userId: targetId, // Often ID is used as userId in this mock setup if discordId isn't available, but let's be careful.
                // In this system, checkPlayerId returns discordId. 
                // Wait, QuickPunishmentModal is called from TicketViewStaff with targetId.
                // It should probably receive the full player object if available.

                characterId: targetId,
                punishmentType: formData.type,
                publicReason: formData.reason,
                internalReason: formData.notes,
                appliedBy: staffName,
                ticketId: ticketId
            };

            await addPunishment(punishmentData);

            // Log in Ticket
            const msg = `**[AÇÃO AUTOMÁTICA]**\n\n**O jogador (ID: ${targetId}) foi punido via Quick Action.**\n\n**Tipo:** ${formData.type.toUpperCase()}\n**Motivo:** ${formData.reason}`;
            await addTicketResponse(ticketId, msg, false, 'SISTEMA');

            // Should we resolve the ticket? Usually report tickets are resolved after action.
            // Let's ask or just do it. Implementation plan said "Register Action", implying it handles it.
            // I'll leave it open or ask user? Plan didn't specify auto-close. I'll add a response.

            onClose();
            alert('Punição aplicada com sucesso!');
        } catch (error) {
            console.error(error);
            alert('Erro ao aplicar punição.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="bg-gray-900 border border-gray-800 text-white sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-500">
                        <Gavel className="w-5 h-5" />
                        Aplicar Punição Rápida
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                        Registrar ação para o ID <strong>{targetId}</strong> ({targetCity}).
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                        <Label>Tipo de Punição</Label>
                        <Select
                            value={formData.type}
                            onValueChange={(val) => handleChange('type', val)}
                        >
                            <SelectTrigger className="bg-gray-800 border-gray-700">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-700 text-white">
                                {PUNISHMENT_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>
                                        {type}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid gap-2">
                        <Label>Motivo</Label>
                        <Input
                            value={formData.reason}
                            onChange={(e) => handleChange('reason', e.target.value)}
                            placeholder="Ex: RDM, VDM, Exploit..."
                            className="bg-gray-800 border-gray-700"
                        />
                    </div>

                    {formData.type === 'Banimento Permanente' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                                <Label>Duração</Label>
                                <Input
                                    type="number"
                                    value={formData.duration}
                                    onChange={(e) => handleChange('duration', e.target.value)}
                                    placeholder="0 = Permanente"
                                    className="bg-gray-800 border-gray-700"
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label>Unidade</Label>
                                <Select
                                    value={formData.durationType}
                                    onValueChange={(val) => handleChange('durationType', val)}
                                >
                                    <SelectTrigger className="bg-gray-800 border-gray-700">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                                        <SelectItem value="days">Dias</SelectItem>
                                        <SelectItem value="hours">Horas</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    <div className="grid gap-2">
                        <Label>Nota Interna (Opcional)</Label>
                        <Textarea
                            value={formData.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            placeholder="Detalhes visíveis apenas para staff..."
                            className="bg-gray-800 border-gray-700"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="border-gray-700 text-gray-400 hover:text-white">
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit} disabled={loading} className="bg-red-600 hover:bg-red-700">
                        {loading ? 'Aplicando...' : 'Confirmar Punição'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default QuickPunishmentModal;
